import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import Highlighter from 'react-highlight-words'
import { Button } from '@cargomart/ui'
import { handleFetchResponse } from 'api/errors'
import IconAccount from 'assets/account-pin-circle-line.svg?react'
import IconArrow from 'assets/arrow-down.svg?react'
import IconArrowBack from 'assets/arrow-left-line.svg?react'
import cn from 'classnames'
import {
  MenuItem,
  MODAL_IDS,
  ModalItem,
  ModalSimple,
  Tab,
  TabContent,
  TabList,
  TabProvider,
  useModal
} from 'components'
import { PlaceholderCell, Search } from 'components/extend'
import { getFormatPhoneNumber } from 'helpers/string'
import { useDebounce, useIsMobile, useOnMount } from 'hooks'
import {
  prepareContactUpdateData,
  useCreateContactMutation,
  useGetContactListQuery,
  useLazyGetContactListQuery
} from 'modules/contact/api'
import { ContactCreateForm } from 'modules/contact/components'
import { getContactFullName, getContactFullPhone } from 'modules/contact/helpers'
import { TContact } from 'modules/contact/types'
import { useLazyGetContactsByCustomerIdQuery, useLazyGetCustomerListQuery } from 'modules/customer/api'
import { AttachBlock } from 'modules/customer/components'
import { getCustomerInnKpp, getCustomerName } from 'modules/customer/helpers'
import { TCustomer } from 'modules/customer/types'
import { useOrderCreateFormData } from 'modules/order/pages'
import { RadioSwitchValue } from 'modules/order/types'
import IconPlus from 'remixicon/icons/System/add-line.svg?react'
import { CourierDeliveryInfo, OrderItemClient } from 'schema'
import { ButtonIcon } from 'shared/components'

import { TabContentWrapper } from '../OrderCreateFormRouteItem/TabContentWrapper/TabContentWrapper'
import { OrderCreateContactModalProps } from './types'

const OrderCreateContactModal: FC<OrderCreateContactModalProps> = (props) => {
  const {
    id,
    onClose,
    pointType,
    onChange,
    handleOpenCustomerAddModal,
    handleAttach,
    setIsPartnerUpdate,
    onPartnerTypeChange
  } = props
  const isMobile = useIsMobile()
  const { openModal, closeModal } = useModal()
  const {
    activeCustomerId,
    setActiveCustomerId,
    activeCustomer,
    setActiveCustomer,
    isContactsByCustomerList,
    setIsContactsByCustomerList,
    isActiveCustomerAddContactAccess,
    setIsActiveCustomerAddContactAccess,
    isAttachBlockFullHeight,
    setIsAttachBlockFullHeight,
    isShowAttachBtn,
    setIsShowAttachBtn
  } = useOrderCreateFormData()
  const [createContact, { isLoading: isCreating }] = useCreateContactMutation()

  const orderCreateAddContactModalId =
    pointType === CourierDeliveryInfo.pointType.DEPARTURE
      ? MODAL_IDS.OrderCreateAddContactDeparture
      : MODAL_IDS.OrderCreateAddContactDestination

  const [debouncedSearchTermContact, setDebouncedSearchTermContact] = useState('')
  const debouncedSearchContact = useDebounce(debouncedSearchTermContact, 500)
  const [searchContact, setSearchContact] = useState(debouncedSearchContact)

  const [debouncedSearchTermCustomer, setDebouncedSearchTermCustomer] = useState('')
  const debouncedSearchCustomer = useDebounce(debouncedSearchTermCustomer, 500)
  const [searchCustomer, setSearchCustomer] = useState(debouncedSearchCustomer)

  const [debouncedSearchTermAttachedContact, setDebouncedSearchTermAttachedContact] = useState('')
  const debouncedSearchAttachedContact = useDebounce(debouncedSearchTermAttachedContact, 500)
  const [searchAttachedContact, setSearchAttachedContact] = useState(debouncedSearchAttachedContact)

  const [orderClientTypeTab, setOrderClientTypeTab] = useState(OrderItemClient.type.LEGAL)
  const [isAttachAction, setIsAttachAction] = useState(false)

  const [getSearchContact, { data: contacts, isLoading: isContactListLoading, isFetching: isContactListFetching }] =
    useLazyGetContactListQuery()

  const { refetch: refetchContacts } = useGetContactListQuery('')

  const [getCustomer, { data: customers, isLoading: isCustomerListLoading, isFetching: isCustomerListFetching }] =
    useLazyGetCustomerListQuery()

  const [
    getContactsByCustomer,
    { data: attachedContacts, isLoading: isAttachedContactsLoading, isFetching: isAttachedContactsFetching }
  ] = useLazyGetContactsByCustomerIdQuery()

  const handleSearchContact = (search: string) => {
    setDebouncedSearchTermContact(search)
    setSearchContact(search)
  }

  const handleSearchCustomer = (search: string) => {
    setDebouncedSearchTermCustomer(search)
    setSearchCustomer(search)
  }

  const handleSearchAttachedContact = (search: string) => {
    setDebouncedSearchTermAttachedContact(search)
    setSearchAttachedContact(search)
  }

  const filteredCustomerList = useMemo(() => {
    if (!customers) {
      return []
    }
    if (!searchCustomer) {
      return customers
    }

    return customers.filter(
      (item) =>
        item.name.toLowerCase().includes(searchCustomer.toLowerCase()) ||
        item?.inn?.toLowerCase().includes(searchCustomer.toLowerCase())
    )
  }, [customers, searchCustomer])

  const filteredAttachedContactList = useMemo(() => {
    if (!attachedContacts) {
      return []
    }
    if (!searchAttachedContact) {
      return attachedContacts
    }

    const mappedAttachedContacts = attachedContacts?.map((item) => ({
      ...item,
      fullname: `${item.lastname} ${item.firstname}${item.secondname ? ` ${item.secondname}` : ''}`
    }))

    return mappedAttachedContacts?.filter((item: any) => {
      const maskedPhone = getFormatPhoneNumber(item.phone)

      return (
        item.fullname.toLowerCase().includes(searchAttachedContact.toLowerCase()) ||
        maskedPhone.includes(searchAttachedContact) ||
        (item.phoneExt ? item.phoneExt.includes(searchAttachedContact) : '')
      )
    })
  }, [attachedContacts, searchAttachedContact])

  useEffect(() => {
    if (debouncedSearchContact.length === 0 || debouncedSearchContact.length > 1) {
      setSearchContact(searchContact)
    }
  }, [debouncedSearchContact, searchContact])

  const handleShowAttachAction = () => {
    setIsAttachAction(true)
  }

  const handleHideAttachAction = () => {
    setIsAttachAction(false)
  }

  const handleSelectContact = useCallback(
    (contact: TContact) => {
      const data = {
        isUser: RadioSwitchValue.no,
        type: activeCustomerId ? OrderItemClient.type.LEGAL : OrderItemClient.type.INDIVIDUAL,
        contactId: contact.id,
        companyId: activeCustomerId || undefined,
        company: activeCustomerId ? activeCustomer : undefined,
        contact: {
          firstname: contact.firstname,
          lastname: contact.lastname,
          secondname: contact.secondname,
          phone: contact?.phone,
          phoneExt: contact?.phoneExt ? contact?.phoneExt : ''
        }
      }
      setIsPartnerUpdate((prev) => !prev)
      onClose()
      // @ts-ignore
      onChange(data)
    },
    [onChange, onClose, activeCustomerId, activeCustomer, setIsPartnerUpdate]
  )

  const handleSelectCustomer = useCallback(
    (customer: TCustomer) => {
      setIsContactsByCustomerList(true)
      handleShowAttachAction()
      setActiveCustomer({
        name: customer.name,
        inn: customer.inn,
        kpp: customer.kpp
      })
      setIsActiveCustomerAddContactAccess(customer.access.addContact)
      setActiveCustomerId(customer.id)
      getContactsByCustomer(customer.id)
    },
    [
      setIsContactsByCustomerList,
      setActiveCustomer,
      setIsActiveCustomerAddContactAccess,
      setActiveCustomerId,
      getContactsByCustomer
    ]
  )

  const handleBack = useCallback(() => {
    setIsContactsByCustomerList(false)
    handleHideAttachAction()
    handleSearchAttachedContact('')
  }, [setIsContactsByCustomerList])

  useEffect(() => {
    getSearchContact(searchContact)
  }, [searchContact]) // eslint-disable-line

  useEffect(() => {
    if (orderClientTypeTab === OrderItemClient.type.INDIVIDUAL) {
      handleHideAttachAction()
      handleSearchAttachedContact('')
    }
  }, [orderClientTypeTab]) // eslint-disable-line

  useEffect(() => {
    if (isContactsByCustomerList) {
      handleShowAttachAction()

      if (activeCustomerId && activeCustomer) {
        setActiveCustomer(activeCustomer)
        setActiveCustomerId(activeCustomerId)
        getContactsByCustomer(activeCustomerId)
      }
    } else {
      getCustomer()
    }
  }, [isContactsByCustomerList]) // eslint-disable-line

  useOnMount(() => {
    setIsContactsByCustomerList(false)
    handleHideAttachAction()
  })

  const skeleton = [...new Array(4)].map((_, i) => (
    <div key={i} className="mb-1 mt-1 pb-2 pt-2">
      <PlaceholderCell className="mb-1 !w-2/6" height={20} />
      <PlaceholderCell className="!w-3/6" height={16} />
    </div>
  ))

  const handleAddContact = useCallback(
    (name: string, data: any) => {
      setIsPartnerUpdate((prev) => !prev)
      onClose()

      // @ts-ignore
      onChange({
        ...data,
        type: activeCustomerId ? OrderItemClient.type.LEGAL : OrderItemClient.type.INDIVIDUAL,
        companyId: activeCustomerId || undefined,
        company: activeCustomerId ? activeCustomer : undefined
      })
    },
    [activeCustomer, activeCustomerId, onChange, onClose, setIsPartnerUpdate]
  )

  const handleHandleAttach = useCallback(
    (customerId: string, contactId: string) => {
      const contact = contacts?.find((contactItem) => contactItem.id === contactId)
      const formattedContact = contact
        ? {
            lastname: contact.lastname,
            firstname: contact.firstname,
            secondname: contact?.secondname,
            phone: contact.phone,
            phoneExt: contact?.phoneExt ? contact?.phoneExt : ''
          }
        : undefined
      const data = {
        isUser: RadioSwitchValue.no,
        type: activeCustomerId ? OrderItemClient.type.LEGAL : OrderItemClient.type.INDIVIDUAL,
        contactId: contact?.id,
        companyId: activeCustomerId || undefined,
        company: activeCustomerId ? activeCustomer : undefined,
        contact: formattedContact
      }
      setIsPartnerUpdate((prev) => !prev)
      onClose()
      handleAttach(customerId, contactId)
      onChange(data)
    },
    [activeCustomer, activeCustomerId, contacts, handleAttach, onChange, onClose, setIsPartnerUpdate]
  )

  const handleOpenContactAddModal = useCallback(() => {
    openModal(orderCreateAddContactModalId)
  }, [openModal, orderCreateAddContactModalId])

  const submitCreateContact = (contact: TContact) =>
    createContact(prepareContactUpdateData(contact)).then(
      handleFetchResponse((response) => {
        closeModal(orderCreateAddContactModalId)
        if (activeCustomerId) {
          handleAttach(activeCustomerId, response.data.contact.id)
        }

        handleAddContact(pointType, {
          type: OrderItemClient.type.INDIVIDUAL,
          isUser: RadioSwitchValue.no,
          contactId: response.data.contact.id,
          companyId: undefined,
          contact: {
            ...response.data.contact,
            phone: response.data.contact?.phone,
            phoneExt: response.data.contact?.phoneExt ? response.data.contact?.phoneExt : ''
          }
        })
        getSearchContact('')
      })
    )

  const contactList = useMemo(() => {
    if (isContactListLoading || isContactListFetching) {
      return skeleton
    }
    if (!contacts || !contacts.length) {
      return (
        <div className="flex h-[22rem] flex-col items-center justify-center gap-4 max-md:h-[60vh]">
          <IconAccount />
          <div className="text-center text-body-lg text-on-surface-variant">
            {searchContact ? 'Контакт не найден' : 'У вас пока не добавлено ни одного контакта'}
          </div>
        </div>
      )
    }

    return contacts.map((contact) => (
      <MenuItem key={contact.id} onClick={() => handleSelectContact(contact)}>
        <Highlighter
          autoEscape
          className="line-clamp-1 w-full text-ellipsis text-body-md text-[#1a1c1e]"
          searchWords={[searchContact]}
          textToHighlight={getContactFullName(contact)}
        />
        <Highlighter
          autoEscape
          className="w-full text-body-sm text-[#6e7076]"
          searchWords={[searchContact]}
          textToHighlight={getContactFullPhone(contact)}
        />
      </MenuItem>
    ))
  }, [contacts, handleSelectContact, isContactListFetching, isContactListLoading, searchContact, skeleton])

  const attachedContactList = useMemo(() => {
    if (isAttachedContactsLoading || isAttachedContactsFetching) {
      return skeleton
    }
    if (!filteredAttachedContactList.length) {
      return (
        <div className="flex h-[22rem] flex-col items-center justify-center gap-4 max-md:h-[60vh]">
          <IconAccount />
          <div className="text-center text-body-lg text-on-surface-variant">
            {searchAttachedContact ? 'Контакт не найден' : 'В организацию пока не добавлено ни одного контакта'}
          </div>
        </div>
      )
    }

    return (
      <div className="px-0 py-2">
        <div className="mb-2 ml-2 mt-2 text-body-sm text-on-surface-variant">Укажите представителя организации:</div>
        {filteredAttachedContactList.map((attachedContact: TContact) => (
          <MenuItem key={attachedContact.id} onClick={() => handleSelectContact(attachedContact)}>
            <Highlighter
              autoEscape
              className="line-clamp-1 w-full text-ellipsis text-body-md text-[#1a1c1e]"
              searchWords={[searchAttachedContact]}
              textToHighlight={getContactFullName(attachedContact)}
            />
            <Highlighter
              autoEscape
              className="w-full text-body-sm text-[#6e7076]"
              searchWords={[searchAttachedContact]}
              textToHighlight={getContactFullPhone(attachedContact)}
            />
          </MenuItem>
        ))}
      </div>
    )
  }, [
    filteredAttachedContactList,
    handleSelectContact,
    isAttachedContactsFetching,
    isAttachedContactsLoading,
    searchAttachedContact,
    skeleton
  ])

  const customerList = useMemo(() => {
    if (isCustomerListLoading || isCustomerListFetching) {
      return skeleton
    }
    if (!filteredCustomerList.length) {
      return (
        <div className="flex h-[22rem] flex-col items-center justify-center gap-4 max-md:h-[60vh]">
          <IconAccount />
          <div className="text-center text-body-lg text-on-surface-variant">
            {searchCustomer ? 'Контрагент не найден' : 'У вас пока не добавлено ни одного контрагента'}
          </div>
        </div>
      )
    }

    return filteredCustomerList.map((customer) => (
      <MenuItem key={customer.id} onClick={() => handleSelectCustomer(customer)}>
        <div className="flex justify-between">
          <div>
            <Highlighter
              autoEscape
              className="line-clamp-1 w-full text-ellipsis text-body-md text-[#1a1c1e]"
              searchWords={[searchCustomer]}
              textToHighlight={getCustomerName(customer)}
            />
            <Highlighter
              autoEscape
              className="w-full text-body-sm text-[#6e7076]"
              searchWords={[searchCustomer]}
              textToHighlight={getCustomerInnKpp(customer)}
            />
          </div>
          <IconArrow className="rotate-[270deg]" />
        </div>
      </MenuItem>
    ))
  }, [
    filteredCustomerList,
    handleSelectCustomer,
    isCustomerListFetching,
    isCustomerListLoading,
    searchCustomer,
    skeleton
  ])

  return (
    <>
      <ModalItem id={orderCreateAddContactModalId}>
        <ModalSimple
          id={orderCreateAddContactModalId}
          onClose={() => closeModal(orderCreateAddContactModalId)}
          size="nm"
          title="Добавление контакта"
        >
          <ContactCreateForm
            createContact={submitCreateContact}
            isCreating={isCreating}
            onClose={() => closeModal(orderCreateAddContactModalId)}
          />
        </ModalSimple>
      </ModalItem>

      <ModalSimple
        isStickyFooter
        isStickyHeader
        bodyClassName="flex flex-col"
        id={id}
        onClose={onClose}
        size="sm"
        title={pointType === CourierDeliveryInfo.pointType.DEPARTURE ? 'Выберите отправителя' : 'Выберите получателя'}
      >
        <TabProvider
          defaultTab={OrderItemClient.type.LEGAL}
          onSetCurrent={(value) => {
            if (value === OrderItemClient.type.INDIVIDUAL) {
              getSearchContact(searchContact)
              setIsContactsByCustomerList(false)
              setOrderClientTypeTab(OrderItemClient.type.INDIVIDUAL)

              setActiveCustomerId('')
            } else {
              getCustomer()
              setOrderClientTypeTab(OrderItemClient.type.LEGAL)
            }
            onPartnerTypeChange?.(value)
          }}
        >
          <TabList className={isMobile ? '!inline-flex' : ''}>
            <Tab className="text-on-surface" tabId={OrderItemClient.type.LEGAL}>
              Организация
            </Tab>
            <Tab className="text-on-surface" tabId={OrderItemClient.type.INDIVIDUAL}>
              Частное лицо
            </Tab>
          </TabList>
          <TabContentWrapper onChangeTab={() => null}>
            <TabContent tabId={OrderItemClient.type.INDIVIDUAL}>
              <div className="max-md:overflow-y-[unset] max-md:shadow-none w-full px-1 py-2">
                <Search
                  isInDetailPage
                  className="mb-2"
                  handleSearch={handleSearchContact}
                  placeholder="ФИО / телефон"
                  searchQuery={searchContact || ''}
                />
                <div className="max-md:min-h-auto h-full min-h-[416px]">{contactList}</div>
              </div>
            </TabContent>
            <TabContent tabId={OrderItemClient.type.LEGAL}>
              <div className="max-md:overflow-y-[unset] max-md:shadow-none w-full px-1 py-2">
                {isContactsByCustomerList ? (
                  <div className="max-md:min-h-auto h-full min-h-[416px]">
                    <div className="relative flex items-center gap-2 pb-2 after:absolute after:bottom-0 after:left-[-28px] after:right-[-28px] after:h-[1px] after:bg-[#d9d9d9] after:content-['']">
                      <ButtonIcon icon={IconArrowBack} onClick={handleBack} size="xs" />
                      <div className="text-body-md text-on-surface">{activeCustomer?.name}</div>
                    </div>
                    {/* eslint-disable-next-line */}
                    {!isAttachedContactsLoading && !isAttachedContactsFetching && !!attachedContacts?.length && (
                      <Search
                        isInDetailPage
                        className="mt-2"
                        handleSearch={handleSearchAttachedContact}
                        placeholder="ФИО / телефон"
                        searchQuery={searchAttachedContact || ''}
                      />
                    )}
                    {attachedContactList}
                  </div>
                ) : (
                  <>
                    <Search
                      isInDetailPage
                      className="mb-2"
                      handleSearch={handleSearchCustomer}
                      placeholder="Организация или ИНН"
                      searchQuery={searchCustomer || ''}
                    />
                    <div className="max-md:min-h-auto h-full min-h-[416px]">{customerList}</div>
                  </>
                )}
              </div>
            </TabContent>
          </TabContentWrapper>
          <div
            className={cn(
              'max-md-[p-4] sticky bottom-0 left-0 z-[5] justify-start border-t border-solid border-t-outline-variant bg-[#fff] px-0 py-4',
              isAttachBlockFullHeight ? 'max-md:h-[100vh]' : 'mt-auto'
            )}
          >
            {isAttachAction ? (
              <AttachBlock
                classNameContainer="!p-0"
                contacts={contacts}
                customerId={activeCustomerId}
                handleAttach={handleHandleAttach}
                handleOpenContactAddModal={handleOpenContactAddModal}
                handleSearchContact={handleSearchContact}
                isAccessAddContact={isActiveCustomerAddContactAccess}
                isContactListFetching={isContactListFetching}
                isContactListLoading={isContactListLoading}
                isShowAttachBtn={isShowAttachBtn}
                refetchContacts={refetchContacts}
                searchQuery={searchContact}
                setIsAttachBlockFullHeight={setIsAttachBlockFullHeight}
                setIsShowAttachBtn={setIsShowAttachBtn}
              />
            ) : (
              <Button
                prefix={<IconPlus containerSize="nm" size="sm" />}
                size="nm"
                variant="text"
                onClick={
                  orderClientTypeTab === OrderItemClient.type.INDIVIDUAL
                    ? handleOpenContactAddModal
                    : handleOpenCustomerAddModal
                }
              >
                {orderClientTypeTab === OrderItemClient.type.INDIVIDUAL ? 'Добавить контакт' : 'Добавить организацию'}
              </Button>
            )}
          </div>
        </TabProvider>
      </ModalSimple>
    </>
  )
}

export default OrderCreateContactModal
